import { store } from 'stores/Store';

interface StreamingFetchOptions {
  message: string;
  fmc_email: string;
  signal?: AbortSignal;
  getAccessToken: () => Promise<string>;
  apiUrl: string;
  scrollToBottom?: () => void;
}

export async function streamingFetch(options: StreamingFetchOptions): Promise<any> {
  const { message, fmc_email, signal, getAccessToken, apiUrl, scrollToBottom } = options;

  const token = await getAccessToken();

  // Build conversation history (last 2 Q&A pairs maximum)
  const conversation_history: Array<{role: string, content: string}> = [];
  const maxHistoryPairs = 2;

  // Note: prompts[0] is always an empty string, so real messages start at index 1
  // Current message is already added to prompts array by Chatbot.tsx before this function is called
  const totalPairs = Math.min(store.prompts.length - 1, store.answers.length); // -1 to skip prompts[0]
  const startIndex = Math.max(0, totalPairs - maxHistoryPairs);

  for (let i = startIndex; i < totalPairs; i++) {
    const promptIndex = i + 1; // Skip prompts[0]
    const answerIndex = i;

    if (store.prompts[promptIndex] && store.answers[answerIndex]) {
      conversation_history.push({
        role: 'user',
        content: store.prompts[promptIndex]
      });
      conversation_history.push({
        role: 'assistant',
        content: store.answers[answerIndex].message || ''
      });
    }
  }

  // Add immediate "Thinking..." step before API call
  store.addAnswer('');
  const answerIndex = store.answers.length - 1;

  const initialThinkingStep = {
    type: 'thinking',
    content: 'Thinking',
    timestamp: new Date().toISOString()
  };
  store.updateThoughtProcess(answerIndex, [initialThinkingStep]);
  // Scroll to show initial thinking step
  scrollToBottom?.();

  // Route to V2 agent when earlyRelease=TRUE, otherwise V1
  const endpoint = store.user?.earlyRelease
    ? '/api/v2/winback_agent_stream'           // V2 agent (winback_agent_v2 microservice)
    : '/api/winback_agent_microservice_stream'; // V1 agent (winback microservice)

  const response = await fetch(`${apiUrl}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      message,
      fmc_email,
      conversation_history,
      conversation_id: store.activeConversationId
    }),
    signal
  });

  if (!response.ok) {
    throw new Error(`Streaming failed: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let streamingMessage = '';
  let thoughtSteps: any[] = [initialThinkingStep];
  let finalPayload: any | null = null;

  // Parse SSE format
  while (reader) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    let currentEvent = '';
    let currentData = '';

    for (const line of lines) {
      if (line.startsWith('event:')) {
        currentEvent = line.substring(6).trim();
      } else if (line.startsWith('data:')) {
        currentData = line.substring(5).trim();
      } else if (line === '' && currentEvent && currentData) {
        // End of SSE message (double newline)
        try {
          const chunk = JSON.parse(currentData);

          if (currentEvent === 'tool_start' || currentEvent === 'tool_complete' || currentEvent === 'crafting_response' || currentEvent === 'error_step') {
            thoughtSteps.push({
              type: currentEvent,
              content: chunk.content,
              timestamp: chunk.timestamp
            });
            // Create a NEW array to ensure MobX detects the change
            store.updateThoughtProcess(answerIndex, [...thoughtSteps]);
            // Scroll to show new thought step
            scrollToBottom?.();
          } else if (currentEvent === 'answer_token') {
            streamingMessage += chunk.content;
            store.updateAnswer(answerIndex, streamingMessage);
            // Scroll to show streaming response text
            scrollToBottom?.();
          } else if (currentEvent === 'complete') {
            finalPayload = chunk.payload;
            if (store.answers[answerIndex] && finalPayload) {
              if (finalPayload.table_json) {
                store.answers[answerIndex].tableJson = finalPayload.table_json;
              }
              if (finalPayload.message_id) {
                store.answers[answerIndex].messageId = finalPayload.message_id;
              }
              // Pass through heatmap data for V2 Hero SKU Heatmap
              if (finalPayload.render_heatmap && finalPayload.heatmap) {
                store.setHeatmap(finalPayload.heatmap);
              }
            }
            // Store conversation_id from backend response
            if (chunk.conversation_id) {
              store.setActiveConversationId(chunk.conversation_id);
            }
          } else if (currentEvent === 'error') {
            // Old error format - throw to stop processing
            console.error('SSE Error:', chunk.error);
            throw new Error(chunk.error);
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', currentEvent, e);
        }

        // Reset for next event
        currentEvent = '';
        currentData = '';
      }
    }
  }

  const result = finalPayload || {
    response: streamingMessage,
    text: '',
    vid: '',
    pic: '',
    waiting: false,
    done: true,
    questions: [],
    table_json: { table_header: [], table_content: [] },
    render_table: false,
    render_heatmap: false,
    heatmap: [],
    message_id: '',
  };

  result._streamingIndex = answerIndex;
  return result;
}
